<template>
  <Layout>
    <PageHeader :title="$t('module.userRole')" :items="breadcrumbs" />
    <div class="card">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-lg-4 mb-2 mb-lg-0">
            <b-input-group>
              <b-form-input
                v-model.trim="searchText"
                type="search"
                :placeholder="$t('label.search')"
                @keyup.enter="applySearch"
              />
              <b-input-group-append
                ><b-button variant="info" @click="applySearch"
                  ><i class="mdi mdi-magnify"></i></b-button
              ></b-input-group-append>
            </b-input-group>
          </div>
          <div class="col-lg-8 d-flex justify-content-lg-end">
            <b-button variant="info" @click="openForm()">
              <i class="mdi mdi-plus mr-1"></i
              >{{ $t("label.createX", [$t("module.userRole")]) }}
            </b-button>
          </div>
        </div>
        <div v-if="!status.list" class="d-flex justify-content-center p-5">
          <b-spinner variant="info" />
        </div>
        <b-alert v-else-if="listError" show variant="danger">
          {{ $t("label.roleLoadError") }}
          <b-button size="sm" variant="outline-danger" @click="loadRoles">{{
            $t("label.retryPermissionDefinitions")
          }}</b-button>
        </b-alert>
        <template v-else>
          <b-table :items="tableRows" :fields="fields" show-empty responsive>
            <template #cell(isActive)="{ item }">
              <b-form-checkbox
                :key="`${item.uuid}-${statusToggleVersion}`"
                :checked="item.isActive"
                switch
                :disabled="!status.save || statusTogglePending"
                @change="confirmStatus(item, $event)"
              >
                {{ item.isActive ? $t("label.active") : $t("label.inactive") }}
              </b-form-checkbox>
            </template>
            <template #cell(actions)="{ item }">
              <a
                href="javascript:void(0);"
                class="text-info"
                @click.stop="openForm(item)"
                ><i class="mdi mdi-pencil-outline font-size-18"></i
              ></a>
              <a
                href="javascript:void(0);"
                class="text-danger ml-2"
                @click.stop="confirmDelete(item)"
                ><i class="mdi mdi-delete-outline font-size-18"></i
              ></a>
            </template>
          </b-table>
          <div
            v-if="pagination.total"
            class="d-flex justify-content-between align-items-center mt-3"
          >
            <small class="text-muted">{{
              $tc("label.results", pagination.total, [pagination.total])
            }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.total"
              :per-page="searchCriteria.pageSize"
              class="mb-0"
              @change="pageNavigation"
            />
          </div>
        </template>
      </div>
    </div>
    <b-modal
      id="role-form"
      :title="
        $t(role.uuid ? 'label.updateX' : 'label.createX', [
          $t('module.userRole'),
        ])
      "
      size="xl"
      hide-footer
      hide-header-close
      centered
      @hidden="closeForm"
      ><b-container fluid>
        <UserRoleForm
          v-if="formVisible"
          :role-u-u-i-d="role.uuid"
          :search-criteria="searchCriteria"
          @done="closeForm"
        /> </b-container
    ></b-modal>
  </Layout>
</template>

<script>
import { mapState } from "vuex";
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { roleMethods } from "@/state/api";
import { ROLE_MODULE } from "@/state/api/role.state";
import UserRoleForm from "@/views/pages/admin/userRole/form";

const defaultCriteria = () => ({
  pageNumber: 1,
  pageSize: 15,
  searchArray: [],
});

export default {
  name: "UserRoleListing",
  page: { title: i18n.t("module.userRole") },
  components: { Layout, PageHeader, UserRoleForm },
  data() {
    return {
      statusToggleVersion: 0,
      statusTogglePending: false,
      searchText: "",
      currentPage: 1,
      formVisible: false,
      role: { uuid: "" },
      searchCriteria: defaultCriteria(),
      breadcrumbs: [
        { text: i18n.t("module.administration"), href: "/administrations" },
        { text: i18n.t("module.userRole"), active: true },
      ],
      fields: [
        { key: "name", label: i18n.t("label.name") },
        { key: "description", label: i18n.t("label.description") },
        { key: "isActive", label: i18n.t("label.status") },
        { key: "createdAt", label: i18n.t("label.createdAt") },
        { key: "actions", label: i18n.t("label.action") },
      ],
    };
  },
  computed: {
    ...mapState(ROLE_MODULE, ["status", "listError"]),
    tableRows() {
      return this.$store.state.api.role.list.data || [];
    },
    pagination() {
      return this.$store.state.api.role.list.pagination || {};
    },
  },
  created() {
    this.loadRoles();
  },
  methods: {
    ...roleMethods,
    loadRoles() {
      return this.getAllRole({ filter: this.searchCriteria });
    },
    applySearch() {
      this.currentPage = 1;
      this.searchCriteria = {
        ...this.searchCriteria,
        pageNumber: 1,
        searchArray: this.searchText ? [this.searchText] : [],
      };
      this.loadRoles();
    },
    pageNavigation(pageNumber) {
      this.currentPage = pageNumber;
      this.searchCriteria = { ...this.searchCriteria, pageNumber };
      this.loadRoles();
    },
    openForm(role = null) {
      this.role = { uuid: role?.uuid || "" };
      this.formVisible = true;
      this.$nextTick(() => this.$bvModal.show("role-form"));
    },
    closeForm() {
      this.$bvModal.hide("role-form");
      this.formVisible = false;
      this.role = { uuid: "" };
    },
    async confirmStatus(role, isActive) {
      if (this.statusTogglePending || !this.status.save) return;
      this.statusTogglePending = true;
      try {
        const action = this.$t(
          isActive ? "label.activate" : "label.deactivate",
        );
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t("label.confirmChangeStatusX", [
            action,
            this.$t("module.userRole"),
          ]),
          {
            title: this.$t("label.confirmStatusChange"),
            okTitle: this.$t("label.confirm"),
            okVariant: "info",
            cancelTitle: this.$t("label.cancel"),
            centered: true,
          },
        );
        if (confirmed) {
          await this.updateRoleStatus({
            uuid: role.uuid,
            isActive,
            filter: this.searchCriteria,
          });
        }
      } finally {
        // Restore the saved status after cancellation or a failed request.
        this.statusToggleVersion++;
        this.statusTogglePending = false;
      }
    },
    async confirmDelete(role) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("label.confirmDeleteX", [this.$t("module.userRole")]),
        {
          title: this.$t("label.delete"),
          okTitle: this.$t("label.delete"),
          okVariant: "danger",
          cancelTitle: this.$t("label.cancel"),
          centered: true,
        },
      );
      if (confirmed) {
        const result = await this.deleteRole({
          uuid: role.uuid,
          filter: this.searchCriteria,
        });
        if (result?.success && this.currentPage > 1 && !this.tableRows.length) {
          this.pageNavigation(this.currentPage - 1);
        }
      }
    },
  },
};
</script>
